import React, { useState } from 'react';

import { RiAddLine } from '@remixicon/react';

import { Field } from '@/design-system/field';
import { Hint } from '@/design-system/hint';
import { Label } from '@/design-system/label';
import { SearchSelect } from '@/design-system/search-select';
import { Tag } from '@/design-system/tag';
import type { RoutePlan } from '@/services/api/routePlans';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import AddressEndpointFields, {
  type EndpointField,
  type EndpointValues,
} from '@/pages/addresses/components/AddressEndpointFields';
import {
  addStop,
  defaultWorkflowFor,
  driverLabel,
  droppedPlanStops,
  MIN_STOPS,
  removeStop,
  syncLinkedStops,
  type StopDraft,
} from '@/pages/shipments/components/direct4wStops';
import type {
  Direct4WStopType,
  ShipmentExim,
} from '@/services/api/shipments';
import {
  WORKFLOW_STOP_INTENT_LABELS,
  WORKFLOW_STOP_TYPE_LABELS,
  type WorkflowDetail,
  type WorkflowStopIntent,
  type WorkflowSummary,
} from '@/services/api/stopWorkflows';

import type { WorkflowsByStopType } from './useDirect4WWorkflows';

/**
 * Direct4WStopsStep — the "Rute & stops" step of every 4W wizard, as a
 * CONTROLLED component over the `StopDraft[]` that `direct4wStops.ts`
 * manipulates. The parent owns the array (it rebuilds it on a delivery-type
 * switch, prefills it from a UJP row or a lane) and the workflow data (see
 * `useDirect4WWorkflows`); this step only edits stops in place and reports the
 * next array through `onChange`, already run through `syncLinkedStops` so a
 * RETURN_CONTAINER leg keeps mirroring the origin.
 *
 * Error keys follow the parent's convention: `${uid}.name`, `${uid}.address`,
 * `${uid}.workflow`.
 */

export interface Direct4WStopsStepProps {
  /** Host (modal) open — gates the address-picker data hooks. */
  open: boolean;
  stops: StopDraft[];
  onChange: (next: StopDraft[]) => void;
  errors: Record<string, string>;
  onClearError: (key: string) => void;
  workflows: WorkflowsByStopType;
  workflowsLoading: boolean;
  wfDetails: Record<string, WorkflowDetail>;
  wfDetailErrors: ReadonlySet<string>;
  /** Delivery direction — narrows the workflow options + default resolution. */
  exim: ShipmentExim;
  /** Whether a client is chosen; workflow pickers are disabled until then. */
  hasClient: boolean;
  /**
   * Human label of the delivery type for the lead-in sentence
   * ("Stop diturunkan dari tipe pengiriman …"). Omit to hide the sentence.
   */
  deliveryTypeLabel?: string;
  /**
   * Fires when ops edit a stop's location by hand — the UJP wizard uses it to
   * mark the stop set as "diubah manual" so lanes stop auto-filling.
   */
  onManualEdit?: () => void;
  /** Optional content rendered above the stop cards (banners etc.). */
  children?: React.ReactNode;
  /**
   * "Isi dari rute" (CR2-D7): the client's active route plans. When set together
   * with `onFillFromPlan`, a plan picker renders above the cards; picking a plan
   * replaces the whole stop set (the host maps it via `shipmentStopsFromPlan`).
   */
  plans?: RoutePlan[];
  plansLoading?: boolean;
  /** The client's pool-leg rule. Since CR-8 it no longer drops any plan stop (billing is
   *  the UJP's question); kept so hosts can keep passing it. */
  chargedPositioning?: boolean;
  onFillFromPlan?: (plan: RoutePlan) => void;
}

const eyebrow =
  'text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

// Adapter: AddressEndpointFields' string coordinates → the numeric draft.
// '' or NaN → undefined so validation still catches "no coordinates".
const toStopCoord = (v: string): number | undefined => {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

const Direct4WStopsStep: React.FC<Direct4WStopsStepProps> = ({
  open,
  stops,
  onChange,
  errors,
  onClearError,
  workflows,
  workflowsLoading,
  wfDetails,
  wfDetailErrors,
  exim,
  hasClient,
  deliveryTypeLabel,
  onManualEdit,
  children,
  plans,
  plansLoading,
  chargedPositioning = false,
  onFillFromPlan,
}) => {
  const locatedCount = stops.filter(
    (s) => s.latitude != null && s.longitude != null
  ).length;

  const [planId, setPlanId] = useState('');
  const selectedPlan = (plans ?? []).find((p) => p.id === planId) ?? null;
  const dropped = selectedPlan
    ? droppedPlanStops(selectedPlan, chargedPositioning)
    : [];

  const patchStop = (uid: string, patch: Partial<StopDraft>) =>
    onChange(
      syncLinkedStops(
        stops.map((s) => (s.uid === uid ? { ...s, ...patch } : s))
      )
    );

  const applyStopEndpointField = (
    uid: string,
    field: EndpointField,
    value: string
  ) => {
    // Editing a mirrored stop by hand takes it off the origin link.
    const unlink: Partial<StopDraft> = { linkedToOrigin: false };
    if (field === 'lat')
      patchStop(uid, { ...unlink, latitude: toStopCoord(value) });
    else if (field === 'long')
      patchStop(uid, { ...unlink, longitude: toStopCoord(value) });
    else patchStop(uid, { ...unlink, [field]: value } as Partial<StopDraft>);
    onClearError(`${uid}.${field === 'name' ? 'name' : 'address'}`);
    onManualEdit?.();
  };

  const applyStopEndpointPatch = (
    uid: string,
    patch: Partial<EndpointValues>
  ) => {
    const p: Partial<StopDraft> = {};
    if (patch.name !== undefined) p.name = patch.name;
    if (patch.address !== undefined) p.address = patch.address;
    if (patch.lat !== undefined) p.latitude = toStopCoord(patch.lat);
    if (patch.long !== undefined) p.longitude = toStopCoord(patch.long);
    p.linkedToOrigin = false;
    patchStop(uid, p);
    onClearError(`${uid}.name`);
    onClearError(`${uid}.address`);
    onManualEdit?.();
  };

  const handleSelectWorkflow = (stop: StopDraft, id: string) => {
    // A manual pick is frozen: an exim flip or a workflow reload never
    // re-resolves it back to a default. It also stops being a plan pin, so a
    // stale hand pick hard-fails on the server (CR5-D3) as it always did.
    patchStop(stop.uid, {
      workflowId: id,
      workflowManual: true,
      workflowFromPlan: false,
    });
    onClearError(`${stop.uid}.workflow`);
  };

  /**
   * The workflows a stop may pick — scoped to the same dimensions the server
   * resolves the default along: the pool is already client + stop type; here we
   * narrow to the chosen direction (this `exim`, plus GENERAL as the
   * direction-agnostic fallback) and, for DROP_OFF, the stop's intent (or
   * GENERIC). So an Export booking never lists the client's Import-only
   * workflows, and the auto-selected default is always one of these rows.
   */
  const workflowOptionsFor = (stop: StopDraft): WorkflowSummary[] =>
    (workflows[stop.type] ?? []).filter((w) => {
      const eximOk = w.exim === exim || w.exim === 'GENERAL';
      const intentOk =
        stop.type === 'DROP_OFF'
          ? w.intent === stop.intent || w.intent === 'GENERIC'
          : true;
      return eximOk && intentOk;
    });

  return (
    <div className="space-y-5">
      {deliveryTypeLabel && (
        <p className="text-[12px] leading-relaxed text-text-sub-600">
          Stop diturunkan dari tipe pengiriman{' '}
          <strong className="text-text-strong-950">{deliveryTypeLabel}</strong>
          . Atur nama, alamat, dan workflow tiap stop.
        </p>
      )}

      {children}

      {onFillFromPlan && hasClient && (
        <div className="rounded-xl border border-stroke-soft-200 bg-bg-weak-50 px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Label id="s4w-plan-label" className="shrink-0">
              Isi dari rute
            </Label>
            <div
              className="flex-1"
              role="group"
              aria-labelledby="s4w-plan-label"
            >
              <SearchSelect
                options={(plans ?? []).map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
                value={planId || undefined}
                onChange={(v) => {
                  const plan = (plans ?? []).find((p) => p.id === String(v));
                  if (plan) {
                    setPlanId(plan.id);
                    onFillFromPlan(plan);
                  }
                }}
                placeholder="Pilih rute tersimpan…"
                loading={!!plansLoading}
                emptyMessage="Belum ada rute untuk client ini"
              />
            </div>
          </div>
          {selectedPlan && dropped.length > 0 && (
            <p className="mt-2 text-[11px] leading-snug text-text-soft-400">
              Stop pool tidak dibawa ke shipment:{' '}
              {dropped.map((s) => s.name).join(', ')}
            </p>
          )}
        </div>
      )}

      {/* Loud, conditional warning: a text-only prefill (UJP row, hand-typed
          address) carries no coordinates, so every such stop is "unlocated"
          until ops picks an exact point. */}
      {stops.length - locatedCount > 0 && (
        <div className="flex items-start justify-between gap-3 rounded-gsm border border-warning-light bg-warning-lighter px-3 py-2.5">
          <div className="flex items-start gap-2">
            <i className="ri-error-warning-fill mt-px text-[16px] text-warning-base" />
            <div>
              <p className="text-[12px] font-bold text-warning-dark">
                {stops.length - locatedCount} dari {stops.length} stop belum
                terkunci ke lokasi
              </p>
              <p className="mt-0.5 text-[11px] leading-snug text-text-sub-600">
                Alamat teks saja belum punya koordinat — pilih lokasi persis di
                tiap stop di bawah. Kalau alamatnya belum ada, tambahkan dulu ke
                master alamat.
              </p>
            </div>
          </div>
          <a
            href="/addresses"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold tracking-tight text-accent hover:underline"
          >
            <RiAddLine className="size-3.5" />
            Tambah alamat
          </a>
        </div>
      )}

      {/* Column legend — what every stop card asks for, in order. */}
      <div
        className="flex items-center gap-2 px-1"
        aria-hidden="true"
      >
        <span className={eyebrow}>Nama tempat</span>
        <span className="text-text-soft-400">·</span>
        <span className={eyebrow}>Alamat</span>
        <span className="text-text-soft-400">·</span>
        <span className={eyebrow}>Workflow</span>
      </div>

      <ol className="space-y-5" aria-label="Daftar stop">
        {stops.map((stop, index) => {
          const n = index + 1;
          const detail = stop.workflowId
            ? wfDetails[stop.workflowId]
            : undefined;
          const summary = (workflows[stop.type] ?? []).find(
            (w) => w.id === stop.workflowId
          );
          // A stop stays "unlocated" until ops picks an exact point (or adds
          // the address to the master).
          const needsLocation =
            stop.latitude == null || stop.longitude == null;
          return (
            <li
              key={stop.uid}
              className="rounded-xl border border-stroke-soft-200"
              aria-label={`Stop ${n} · ${driverLabel(stop)}`}
            >
              <div className="flex items-center gap-3 border-b border-stroke-soft-200 bg-bg-weak-50 px-4 py-3 rounded-t-xl">
                <span className="font-mono text-[12px] font-semibold text-accent tabular-nums">
                  {String(n).padStart(2, '0')}
                </span>
                <SegmentedControl
                  value={stop.type}
                  size="sm"
                  aria-label={`Tipe stop ${n}`}
                  onValueChange={(v: string) => {
                    if (v) {
                      patchStop(stop.uid, {
                        type: v as Direct4WStopType,
                        // Switching type invalidates a workflow picked for
                        // the other type (a plan pin included).
                        workflowId: '',
                        workflowFromPlan: false,
                      });
                    }
                  }}
                >
                  <SegmentedItem value="PICKUP">
                    {WORKFLOW_STOP_TYPE_LABELS.PICKUP}
                  </SegmentedItem>
                  <SegmentedItem value="DROP_OFF">
                    {WORKFLOW_STOP_TYPE_LABELS.DROP_OFF}
                  </SegmentedItem>
                </SegmentedControl>
                <div className="ml-auto flex items-center gap-1.5">
                  {needsLocation && (
                    <span className="inline-flex items-center gap-1 rounded-gsm border border-warning-light bg-warning-lighter px-2 py-0.5 text-[11px] font-bold tracking-tight text-warning-dark">
                      <i className="ri-map-pin-2-fill text-[12px]" />
                      Perlu lokasi
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5 rounded-gsm bg-bg-white-0 border border-stroke-soft-200 px-2 py-0.5 text-[11px] font-semibold tracking-tight text-text-sub-600">
                    <i className="ri-truck-line text-[13px]" />
                    {driverLabel(stop)}
                  </span>
                  <button
                    type="button"
                    aria-label={`Hapus stop ${n}`}
                    title={
                      stops.length <= MIN_STOPS
                        ? `Minimal ${MIN_STOPS} stop`
                        : 'Hapus stop'
                    }
                    disabled={stops.length <= MIN_STOPS}
                    onClick={() => onChange(removeStop(stops, stop.uid))}
                    className="inline-flex size-6 items-center justify-center rounded-gsm text-text-sub-600 hover:bg-bg-weak-50 hover:text-error-base disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-delete-bin-line text-[14px]" />
                  </button>
                </div>
              </div>

              <div className="space-y-4 px-4 py-4">
                {stop.type === 'DROP_OFF' && (
                  <Field>
                    <Label htmlFor={`${stop.uid}-intent`}>Tujuan drop off</Label>
                    <SegmentedControl
                      id={`${stop.uid}-intent`}
                      value={stop.intent}
                      onValueChange={(v: string) => {
                        if (!v) return;
                        const nextIntent = v as WorkflowStopIntent;
                        const patch: Partial<StopDraft> = {
                          intent: nextIntent,
                        };
                        // The container returns to where it was picked up,
                        // so switching to "balikin kontainer" links this
                        // stop to the origin. Switching back to a delivery
                        // clears the mirrored address rather than leaving
                        // the port's on a customer stop.
                        if (nextIntent === 'RETURN_CONTAINER') {
                          patch.linkedToOrigin = true;
                        } else if (stop.linkedToOrigin) {
                          patch.linkedToOrigin = false;
                          patch.name = '';
                          patch.address = '';
                          patch.latitude = undefined;
                          patch.longitude = undefined;
                        }
                        // Drop a picked workflow that no longer fits the
                        // new intent (a GENERIC one still applies).
                        const picked = workflows.DROP_OFF.find(
                          (w) => w.id === stop.workflowId
                        );
                        if (
                          picked &&
                          picked.intent !== nextIntent &&
                          picked.intent !== 'GENERIC'
                        ) {
                          // Swap straight to the new intent's default
                          // instead of blanking the field — an empty picker
                          // after changing tujuan reads as a bug.
                          patch.workflowId =
                            defaultWorkflowFor(
                              { ...stop, intent: nextIntent },
                              workflows,
                              exim
                            )?.id ?? '';
                          patch.workflowManual = false;
                          patch.workflowFromPlan = false;
                        }
                        patchStop(stop.uid, patch);
                      }}
                      className="w-full [&>*]:flex-1"
                    >
                      <SegmentedItem value="UNLOAD">
                        {WORKFLOW_STOP_INTENT_LABELS.UNLOAD}
                      </SegmentedItem>
                      <SegmentedItem value="RETURN_CONTAINER">
                        {WORKFLOW_STOP_INTENT_LABELS.RETURN_CONTAINER}
                      </SegmentedItem>
                      <SegmentedItem value="GENERIC">
                        {WORKFLOW_STOP_INTENT_LABELS.GENERIC}
                      </SegmentedItem>
                    </SegmentedControl>
                    <Hint>
                      {stop.intent === 'RETURN_CONTAINER' ? (
                        <span className="text-amber-700">
                          Label driver: <strong>Drop</strong> (retur kontainer)
                          {stop.linkedToOrigin && (
                            <>
                              {' · '}alamat disalin dari origin — edit untuk
                              pakai lokasi lain
                            </>
                          )}
                        </span>
                      ) : stop.intent === 'GENERIC' ? (
                        <>
                          Label driver: <strong>Delivery</strong> (drop umum —
                          dikirim tanpa intent, pakai workflow default umum)
                        </>
                      ) : (
                        <>
                          Label driver: <strong>Delivery</strong> (bongkar)
                        </>
                      )}
                    </Hint>
                  </Field>
                )}

                {/* Right where ops edits: flag an unlocated stop so a text-only
                    address is never left as bare text. */}
                {needsLocation && (
                  <div className="flex items-start gap-2 rounded-gsm border border-warning-light bg-warning-lighter px-3 py-2">
                    <i className="ri-error-warning-fill mt-px text-[15px] text-warning-base" />
                    <p className="text-[11px] leading-snug text-warning-dark">
                      Alamat ini belum punya titik lokasi. Pilih lokasi persis
                      lewat kolom alamat di bawah.{' '}
                      <a
                        href="/addresses"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-accent hover:underline"
                      >
                        Belum ada di master? Tambah alamat
                      </a>
                      .
                    </p>
                  </div>
                )}

                {/* Address entry (manual only). 4W stops are plain addresses:
                    the spec defers hub attribution to v2 and the BE stores
                    just address + lat/long per stop — no hub/client source. */}
                <AddressEndpointFields
                  role={stop.type === 'PICKUP' ? 'origin' : 'destination'}
                  modes={['manual']}
                  label={`Lokasi stop · ${driverLabel(stop)}`}
                  color={stop.type === 'PICKUP' ? '#5E2AAC' : '#1FC16B'}
                  open={open}
                  idPrefix={`${stop.uid}-loc`}
                  ariaLabelPrefix={`Stop ${n}`}
                  placeholders={{
                    name: 'mis. Gudang Karawang',
                    address: 'mis. Jl. Permata Raya Lot 12, KIIC Karawang',
                  }}
                  values={{
                    name: stop.name,
                    address: stop.address,
                    lat: stop.latitude != null ? String(stop.latitude) : '',
                    long:
                      stop.longitude != null ? String(stop.longitude) : '',
                  }}
                  errors={{
                    name: errors[`${stop.uid}.name`],
                    address: errors[`${stop.uid}.address`],
                  }}
                  onFieldChange={(field, value) =>
                    applyStopEndpointField(stop.uid, field, value)
                  }
                  onPatch={(patch) => applyStopEndpointPatch(stop.uid, patch)}
                />

                <Field>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`${stop.uid}-workflow`}>Workflow</Label>
                    {stop.workflowFromPlan && (
                      <Tag
                        variant="gray"
                        size="xs"
                        data-testid={`${stop.uid}-workflow-from-plan`}
                      >
                        dari rute
                      </Tag>
                    )}
                  </div>
                  <SearchSelect
                    options={workflowOptionsFor(stop).map((w) => ({
                      value: w.id,
                      // Several workflows can be active per slot now, so
                      // say which one ops would have got automatically.
                      label: w.isDefault ? `${w.name} · default` : w.name,
                    }))}
                    value={stop.workflowId || undefined}
                    selectedLabelFallback={summary?.name}
                    onChange={(value) =>
                      handleSelectWorkflow(stop, String(value))
                    }
                    placeholder={
                      !hasClient ? 'Pilih client dulu…' : 'Pilih workflow…'
                    }
                    disabled={!hasClient}
                    loading={workflowsLoading}
                    emptyMessage={
                      !hasClient
                        ? 'Pilih client dulu'
                        : `Belum ada workflow ${
                            WORKFLOW_STOP_TYPE_LABELS[stop.type]
                          } aktif`
                    }
                  />
                  {errors[`${stop.uid}.workflow`] && (
                    <Hint tone="error">{errors[`${stop.uid}.workflow`]}</Hint>
                  )}
                  {/* Read-only milestone preview */}
                  {stop.workflowId && (
                    <div className="mt-2 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2">
                      {detail ? (
                        <>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {detail.milestones.length === 0 && (
                              <span className="text-[11px] text-text-sub-600">
                                Tidak ada milestone.
                              </span>
                            )}
                            {detail.milestones.map((m, i) => (
                              <React.Fragment key={m.id}>
                                {i > 0 && (
                                  <i className="ri-arrow-right-s-line text-[13px] text-text-soft-400" />
                                )}
                                <span className="inline-flex items-center rounded-gsm bg-bg-white-0 border border-stroke-soft-200 px-2 py-0.5 text-[11px] font-medium tracking-tight text-text-strong-950">
                                  {m.name}
                                </span>
                              </React.Fragment>
                            ))}
                          </div>
                          <p className="mt-1.5 text-[10px] text-text-sub-600">
                            Untuk mengubah langkah/field → Master → Workflow
                          </p>
                        </>
                      ) : wfDetailErrors.has(stop.workflowId) ? (
                        <span className="text-[11px] text-error-base">
                          Preview workflow gagal dimuat. Workflow-nya tetap
                          dipakai saat shipment dibuat.
                        </span>
                      ) : (
                        <span className="text-[11px] text-text-sub-600">
                          Memuat preview…
                        </span>
                      )}
                    </div>
                  )}
                </Field>
              </div>
            </li>
          );
        })}
      </ol>

      <button
        type="button"
        onClick={() => onChange(addStop(stops))}
        className="inline-flex items-center gap-1.5 rounded-gsm border border-dashed border-stroke-soft-200 px-3 py-2 text-[12px] font-semibold tracking-tight text-text-sub-600 hover:border-accent hover:text-accent"
      >
        <RiAddLine className="size-3.5" />
        Tambah stop
      </button>

      {/* Alur driver chip chain */}
      <div className="rounded-xl border border-stroke-soft-200 bg-bg-weak-50 px-4 py-3">
        <p className={`${eyebrow} mb-2`}>Alur driver</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {stops.map((stop, i) => (
            <React.Fragment key={stop.uid}>
              {i > 0 && (
                <i className="ri-arrow-right-line text-[14px] text-text-soft-400" />
              )}
              <span
                className={`inline-flex items-center rounded-gsm border px-2 py-0.5 text-[12px] font-semibold tracking-tight ${
                  driverLabel(stop) === 'Drop'
                    ? 'border-amber-300 bg-amber-50 text-amber-700'
                    : 'border-stroke-soft-200 bg-bg-white-0 text-text-strong-950'
                }`}
              >
                {driverLabel(stop)}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Direct4WStopsStep;
